import { el } from "../../utils/dom";

export interface SliderSegment {
    key: string;
    label: string;
    color: string;
    value: number; // 0-1
}

export interface MultiKnobSliderOptions {
    segments: SliderSegment[];
    onChange: (values: Record<string, number>) => void;
    height?: number;
}

/**
 * Create a multi-knob slider where segments always total 100%
 * Moving one handle proportionally adjusts adjacent segments
 */
export function createMultiKnobSlider(options: MultiKnobSliderOptions): HTMLElement {
    const { segments, onChange, height = 32 } = options;

    // Clone segments to avoid mutating original
    let currentValues = segments.map(s => ({ ...s }));

    const container = el("div", {
        style: `
            position: relative;
            width: 100%;
            user-select: none;
        `
    });

    // Legend showing current values
    const legend = el("div", {
        style: "display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 12px;"
    });

    function updateLegend() {
        legend.innerHTML = "";
        for (const seg of currentValues) {
            const item = el("div", {
                style: "display: flex; align-items: center; gap: 4px;"
            });
            const colorDot = el("div", {
                style: `width: 10px; height: 10px; border-radius: 50%; background: ${seg.color};`
            });
            const text = el("span", {}, `${seg.label}: ${Math.round(seg.value * 100)}%`);
            item.append(colorDot, text);
            legend.appendChild(item);
        }
    }

    // Track container
    const track = el("div", {
        style: `
            position: relative;
            width: 100%;
            height: ${height}px;
            background: var(--border);
            border-radius: ${height / 2}px;
            overflow: hidden;
            cursor: pointer;
        `
    });

    // Segment fills
    const segmentEls: HTMLElement[] = [];

    function updateSegments() {
        // Clear existing
        track.innerHTML = "";
        segmentEls.length = 0;

        let leftPos = 0;
        for (let i = 0; i < currentValues.length; i++) {
            const seg = currentValues[i]!;
            const widthPercent = seg.value * 100;

            const segEl = el("div", {
                style: `
                    position: absolute;
                    left: ${leftPos}%;
                    width: ${widthPercent}%;
                    height: 100%;
                    background: ${seg.color};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 11px;
                    font-weight: 600;
                    text-shadow: 0 1px 2px rgba(0,0,0,0.3);
                    transition: width 0.05s ease, left 0.05s ease;
                    overflow: hidden;
                    white-space: nowrap;
                `
            });

            // Show label if segment is wide enough
            if (widthPercent > 15) {
                segEl.textContent = `${Math.round(seg.value * 100)}%`;
            }

            segmentEls.push(segEl);
            track.appendChild(segEl);
            leftPos += widthPercent;
        }

        // Add handles between segments
        leftPos = 0;
        for (let i = 0; i < currentValues.length - 1; i++) {
            leftPos += currentValues[i]!.value * 100;
            const handle = createHandle(i, leftPos);
            track.appendChild(handle);
        }

        updateLegend();
    }

    function createHandle(index: number, leftPercent: number): HTMLElement {
        const handle = el("div", {
            "data-handle-index": String(index),
            style: `
                position: absolute;
                left: calc(${leftPercent}% - 8px);
                top: 50%;
                transform: translateY(-50%);
                width: 16px;
                height: ${height + 8}px;
                background: white;
                border: 2px solid var(--fg);
                border-radius: 4px;
                cursor: ew-resize;
                z-index: 10;
                box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                transition: transform 0.1s ease;
            `
        });

        handle.addEventListener("mouseenter", () => {
            handle.style.transform = "translateY(-50%) scale(1.1)";
        });
        handle.addEventListener("mouseleave", () => {
            handle.style.transform = "translateY(-50%)";
        });

        handle.addEventListener("mousedown", (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const trackRect = track.getBoundingClientRect();
            const trackWidth = trackRect.width;

            const onMouseMove = (moveE: MouseEvent) => {
                const x = moveE.clientX - trackRect.left;
                const percent = Math.max(0, Math.min(100, (x / trackWidth) * 100)) / 100;

                // Calculate min/max bounds for this handle
                let minBound = 0.05; // Minimum 5% for first segment
                let maxBound = 0.95; // Maximum 95% for last segment

                for (let i = 0; i < index; i++) {
                    minBound = Math.max(minBound, sumValues(0, i + 1) + 0.05);
                }
                for (let i = index + 2; i < currentValues.length; i++) {
                    maxBound = Math.min(maxBound, 1 - sumValues(i, currentValues.length) - 0.05);
                }

                // Clamp to bounds
                const clampedPercent = Math.max(minBound, Math.min(maxBound, percent));

                // Calculate new values
                const leftSum = sumValues(0, index);
                const rightSum = sumValues(index + 2, currentValues.length);

                // The segment before the handle
                const newLeftValue = clampedPercent - sumValues(0, index);
                // The segment after the handle
                const newRightValue = 1 - clampedPercent - rightSum;

                if (newLeftValue >= 0.05 && newRightValue >= 0.05) {
                    currentValues[index]!.value = newLeftValue;
                    currentValues[index + 1]!.value = newRightValue;
                    updateSegments();
                    emitChange();
                }
            };

            const onMouseUp = () => {
                document.removeEventListener("mousemove", onMouseMove);
                document.removeEventListener("mouseup", onMouseUp);
            };

            document.addEventListener("mousemove", onMouseMove);
            document.addEventListener("mouseup", onMouseUp);
        });

        return handle;
    }

    function sumValues(start: number, end: number): number {
        let sum = 0;
        for (let i = start; i < end && i < currentValues.length; i++) {
            sum += currentValues[i]!.value;
        }
        return sum;
    }

    function emitChange() {
        const values: Record<string, number> = {};
        for (const seg of currentValues) {
            values[seg.key] = seg.value;
        }
        onChange(values);
    }

    // Click on track to adjust nearest handle
    track.addEventListener("click", (e: MouseEvent) => {
        if ((e.target as HTMLElement).hasAttribute("data-handle-index")) return;

        const trackRect = track.getBoundingClientRect();
        const clickPercent = (e.clientX - trackRect.left) / trackRect.width;

        // Find which segment was clicked
        let cumulative = 0;
        for (let i = 0; i < currentValues.length; i++) {
            const nextCumulative = cumulative + currentValues[i]!.value;
            if (clickPercent < nextCumulative) {
                // Clicked in segment i, adjust it
                const segmentMid = cumulative + currentValues[i]!.value / 2;

                if (i > 0 && clickPercent < segmentMid) {
                    // Shrink from left (move left handle right)
                    const handleIndex = i - 1;
                    const change = 0.05;
                    if (currentValues[i]!.value > 0.1) {
                        currentValues[i]!.value -= change;
                        currentValues[handleIndex]!.value += change;
                        updateSegments();
                        emitChange();
                    }
                } else if (i < currentValues.length - 1 && clickPercent >= segmentMid) {
                    // Shrink from right (move right handle left)
                    const change = 0.05;
                    if (currentValues[i]!.value > 0.1) {
                        currentValues[i]!.value -= change;
                        currentValues[i + 1]!.value += change;
                        updateSegments();
                        emitChange();
                    }
                }
                break;
            }
            cumulative = nextCumulative;
        }
    });

    // Public method to set values programmatically
    (container as any).setValues = (newValues: Record<string, number>) => {
        for (const seg of currentValues) {
            if (newValues[seg.key] !== undefined) {
                seg.value = newValues[seg.key]!;
            }
        }
        // Normalize to ensure sum is 1
        const total = currentValues.reduce((sum, s) => sum + s.value, 0);
        if (total > 0) {
            for (const seg of currentValues) {
                seg.value = seg.value / total;
            }
        }
        updateSegments();
    };

    updateLegend();
    updateSegments();

    container.append(legend, track);
    return container;
}
